import React from 'react';
import { Button, Form, Input, Alert, Card, Typography } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import { passwordRules, PASSWORD_REQUIREMENTS } from './passwordValidation';

const { Title, Text } = Typography;

interface ChangePasswordFormProps {
  onSubmit?: (values: {
    currentPassword: string;
    newPassword: string;
    confirmPassword: string;
  }) => Promise<void>;
  loading?: boolean;
  error?: string;
  success?: string;
}

export default function ChangePasswordForm({
  onSubmit,
  loading = false,
  error,
  success,
}: ChangePasswordFormProps) {
  const [form] = Form.useForm();

  const handleSubmit = async (values: {
    currentPassword: string;
    newPassword: string;
    confirmPassword: string;
  }) => {
    if (onSubmit) {
      await onSubmit(values);
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        backgroundColor: '#f0f2f5',
        padding: '2rem',
      }}
    >
      <Card
        style={{
          width: '100%',
          maxWidth: 500,
          padding: '2rem',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        }}
      >
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <LockOutlined style={{ fontSize: '3rem', color: '#93c90e' }} />
          <Title level={2} style={{ marginTop: '1rem', marginBottom: '0.5rem' }}>
            Change Password
          </Title>
          <Text type="secondary">Please enter your current password and choose a new one</Text>
        </div>

        {error && (
          <Alert
            message="Error"
            description={error}
            type="error"
            closable
            style={{ marginBottom: '1.5rem' }}
            showIcon
          />
        )}

        {success && (
          <Alert
            message="Success"
            description={success}
            type="success"
            closable
            style={{ marginBottom: '1.5rem' }}
            showIcon
          />
        )}

        <Form
          form={form}
          name="change-password"
          onFinish={handleSubmit}
          layout="vertical"
          size="large"
          disabled={loading}
        >
          <Form.Item
            name="currentPassword"
            label="Current Password"
            rules={[
              {
                required: true,
                message: 'Please enter your current password',
              },
            ]}
          >
            <Input.Password placeholder="Enter your current password" prefix={<LockOutlined />} />
          </Form.Item>

          <Form.Item
            name="newPassword"
            label="New Password"
            rules={[
              ...passwordRules,
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const currentPassword = getFieldValue('currentPassword');
                  if (!value || !currentPassword || value !== currentPassword) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    new Error('New password must be different from current password'),
                  );
                },
              }),
            ]}
            hasFeedback
          >
            <Input.Password placeholder="Create a new password" prefix={<LockOutlined />} />
          </Form.Item>

          <Form.Item
            name="confirmPassword"
            label="Confirm New Password"
            dependencies={['newPassword']}
            rules={[
              {
                required: true,
                message: 'Please confirm your new password',
              },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Passwords do not match'));
                },
              }),
            ]}
            hasFeedback
          >
            <Input.Password placeholder="Confirm your new password" prefix={<LockOutlined />} />
          </Form.Item>

          <div style={{ marginTop: '1rem', marginBottom: '1rem' }}>
            <Text type="secondary" style={{ fontSize: '0.875rem' }}>
              Password requirements:
            </Text>
            <ul style={{ marginTop: '0.5rem', paddingLeft: '1.5rem', fontSize: '0.875rem' }}>
              {PASSWORD_REQUIREMENTS.map((req) => (
                <li key={req}>{req}</li>
              ))}
            </ul>
          </div>

          <Form.Item style={{ marginBottom: 0 }}>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              block
              size="large"
              style={{
                backgroundColor: '#93c90e',
                borderColor: '#93c90e',
                fontWeight: 500,
              }}
            >
              {loading ? 'Changing Password...' : 'Change Password'}
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
}
